interface VCardUrl {
  value: string;
  type?: string;
}

interface VCardData {
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  jobTitle?: string;
  companyName?: string;
  avatarUrl?: string;
  websiteUrl?: string;
  urls?: VCardUrl[];
  note?: string;
  generatorLabel?: string;
  revisionDate?: Date;
}

export class VCardUtils {
  static downloadVCard(data: VCardData): void {
    const {
      firstName,
      lastName,
      email,
      phone,
      jobTitle,
      companyName,
      avatarUrl,
      websiteUrl,
      urls,
      note,
      generatorLabel,
      revisionDate,
    } = data;

    const lines = [
      "BEGIN:VCARD",
      "VERSION:3.0",
      `FN:${VCardUtils.sanitizeLineValue(`${firstName} ${lastName}`)}`,
      `N:${VCardUtils.sanitizeLineValue(
        lastName
      )};${VCardUtils.sanitizeLineValue(firstName)};;;`,
      `EMAIL:${VCardUtils.sanitizeLineValue(email)}`,
    ];

    if (phone) {
      lines.push(`TEL:${VCardUtils.sanitizeLineValue(phone)}`);
    }

    if (jobTitle) {
      lines.push(`TITLE:${VCardUtils.sanitizeLineValue(jobTitle)}`);
    }

    if (companyName) {
      lines.push(`ORG:${VCardUtils.sanitizeLineValue(companyName)}`);
    }

    if (websiteUrl) {
      lines.push(`URL:${VCardUtils.sanitizeLineValue(websiteUrl)}`);
    }

    if (urls) {
      urls
        .filter((entry) => Boolean(entry?.value))
        .forEach((entry) => {
          const trimmedValue = entry.value.trim();
          if (!trimmedValue) {
            return;
          }

          const sanitizedValue = VCardUtils.sanitizeLineValue(trimmedValue);
          const sanitizedType = entry.type
            ? VCardUtils.sanitizeLineValue(entry.type)
            : undefined;

          if (sanitizedType) {
            lines.push(`URL;TYPE=${sanitizedType}:${sanitizedValue}`);
          } else {
            lines.push(`URL:${sanitizedValue}`);
          }
        });
    }

    if (avatarUrl) {
      lines.push(`PHOTO;VALUE=URI:${VCardUtils.sanitizeLineValue(avatarUrl)}`);
    }

    if (note) {
      const sanitizedNote = VCardUtils.sanitizeNote(note);
      if (sanitizedNote) {
        lines.push(`NOTE:${sanitizedNote}`);
      }
    }

    const generator = generatorLabel?.trim();
    if (generator) {
      lines.push(`X-GENERATOR:${VCardUtils.sanitizeLineValue(generator)}`);
    }

    const revision = revisionDate ?? new Date();
    lines.push(`REV:${VCardUtils.formatRevisionDate(revision)}`);

    lines.push("END:VCARD");

    const vCardContent = lines.join("\r\n");

    const blob = new Blob([vCardContent], { type: "text/vcard;charset=utf-8" });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${VCardUtils.sanitizeFilename(
      `${firstName}_${lastName}_contact`
    )}.vcf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }

  private static sanitizeLineValue(value: string): string {
    return value.replace(/\r?\n/g, " ").trim();
  }

  private static sanitizeNote(note: string): string {
    return note
      .replace(/<[^>]*>/g, " ")
      .replace(/\r?\n/g, " ")
      .replace(/\s+/g, " ")
      .trim();
  }

  private static formatRevisionDate(revisionDate: Date): string {
    return revisionDate
      .toISOString()
      .replace(/[-:]/g, "")
      .split(".")[0]
      .concat("Z");
  }

  private static sanitizeFilename(filename: string): string {
    return filename.replace(/[^a-z0-9-_]+/gi, "_");
  }
}
